/**
 * DeliveryAssignmentPage — `/delivery/assignment` minimal dispatch tool.
 *
 * Picks an existing DRAFT route, picks a driver, calls
 * `POST /v1/routes/:id/dispatch`. The "auto-assign" affordance is now
 * wired live to `POST /v1/routes/:id/auto-assign` — the policy is
 * round-robin biased to the lightest-loaded ACTIVE driver in scope.
 */
import { type FormEvent, type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  type DeliveryRoute,
  autoAssignRoute,
  dispatchRoute,
  listDrivers,
  listRoutes,
  listVehicles,
} from '../../lib/api/delivery';
import { PageHeader, StatusBadge } from '../shared/StaffUI';

export function DeliveryAssignmentPage(): JSX.Element {
  const qc = useQueryClient();
  const [routeId, setRouteId] = useState<string>('');
  const [driverId, setDriverId] = useState<string>('');
  const [vehicleId, setVehicleId] = useState<string>('');
  const [outcome, setOutcome] = useState<
    | { kind: 'ok'; route: DeliveryRoute; chosenDriverId?: string }
    | { kind: 'err'; message: string }
    | null
  >(null);

  const draftRoutes = useQuery({
    queryKey: ['delivery', 'routes', 'draft', { pageSize: 50 }],
    queryFn: () => listRoutes({ status: 'DRAFT', pageSize: 50 }),
    retry: false,
    staleTime: 15_000,
  });
  const drivers = useQuery({
    queryKey: ['delivery', 'drivers', 'active', { pageSize: 50 }],
    queryFn: () => listDrivers({ status: 'ACTIVE', pageSize: 50 }),
    retry: false,
    staleTime: 15_000,
  });
  const vehicles = useQuery({
    queryKey: ['delivery', 'vehicles', 'active', { pageSize: 50 }],
    queryFn: () => listVehicles({ status: 'ACTIVE', pageSize: 50 }),
    retry: false,
    staleTime: 15_000,
  });

  const dispatchMutation = useMutation({
    mutationFn: () => dispatchRoute(routeId, { driverId, ...(vehicleId !== '' && { vehicleId }) }),
    onSuccess: (route) => {
      setOutcome({ kind: 'ok', route });
      void qc.invalidateQueries({ queryKey: ['delivery', 'routes'] });
    },
    onError: (err) => {
      setOutcome({ kind: 'err', message: err instanceof Error ? err.message : 'Dispatch failed' });
    },
  });

  const autoAssignMutation = useMutation({
    mutationFn: () => autoAssignRoute(routeId),
    onSuccess: ({ chosenDriverId, ...route }) => {
      setOutcome({ kind: 'ok', route, chosenDriverId });
      setDriverId(chosenDriverId);
      void qc.invalidateQueries({ queryKey: ['delivery', 'routes'] });
    },
    onError: (err) => {
      setOutcome({ kind: 'err', message: err instanceof Error ? err.message : 'Auto-assign failed' });
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (routeId === '' || driverId === '') return;
    setOutcome(null);
    dispatchMutation.mutate();
  }

  const denied = (q: { isError: boolean; error: unknown }): boolean =>
    q.isError && q.error instanceof ApiError && (q.error.status === 401 || q.error.status === 403);

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader
        title="Delivery Assignment"
        description="Dispatch a draft route. Manual and auto-assign are both supported."
      />

      <form onSubmit={onSubmit} className="bg-white border border-border rounded-xl p-5 space-y-4">
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Route (DRAFT)</label>
          <select
            value={routeId}
            onChange={(e) => setRouteId(e.target.value)}
            required
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-white"
            data-testid="assign-route-select"
          >
            <option value="">Pick a route…</option>
            {(draftRoutes.data?.items ?? []).map((r) => (
              <option key={r.id} value={r.id}>{r.routeCode} · {r.stopCount} stops</option>
            ))}
          </select>
          {denied(draftRoutes) ? (
            <p className="text-xs text-amber-700 mt-1">Permission required: <code className="font-mono">Delivery/readRoute</code></p>
          ) : null}
        </div>

        <div>
          <label className="block text-xs font-medium text-muted mb-1">Driver</label>
          <select
            value={driverId}
            onChange={(e) => setDriverId(e.target.value)}
            required
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-white"
            data-testid="assign-driver-select"
          >
            <option value="">Pick a driver…</option>
            {(drivers.data?.items ?? []).map((d) => (
              <option key={d.id} value={d.id}>{d.fullName} · {d.driverNumber}</option>
            ))}
          </select>
          {denied(drivers) ? (
            <p className="text-xs text-amber-700 mt-1">Permission required: <code className="font-mono">Delivery/readDriver</code></p>
          ) : null}
        </div>

        <div>
          <label className="block text-xs font-medium text-muted mb-1">Vehicle (optional)</label>
          <select
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-white"
          >
            <option value="">No vehicle override</option>
            {(vehicles.data?.items ?? []).map((v) => (
              <option key={v.id} value={v.id}>{v.registration} · {v.kind}</option>
            ))}
          </select>
        </div>

        <div className="pt-2 flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={dispatchMutation.isPending || routeId === '' || driverId === ''}
            data-testid="dispatch-route-btn"
            className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {dispatchMutation.isPending ? 'Dispatching…' : 'Dispatch route'}
          </button>
          <button
            type="button"
            disabled={autoAssignMutation.isPending || routeId === ''}
            onClick={() => {
              setOutcome(null);
              autoAssignMutation.mutate();
            }}
            data-testid="auto-assign-btn"
            className="px-4 py-2 border border-border text-sm font-semibold rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {autoAssignMutation.isPending ? 'Auto-assigning…' : 'Auto-assign (lightest-loaded driver)'}
          </button>
        </div>

        {outcome?.kind === 'ok' ? (
          <div data-testid="dispatch-success" className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm">
            <p className="font-semibold text-emerald-900">
              {outcome.chosenDriverId ? 'Auto-assigned.' : 'Dispatched.'}
            </p>
            <p className="text-xs text-emerald-800 mt-1">
              <code className="font-mono">{outcome.route.routeCode}</code> · <StatusBadge status={outcome.route.status} />
              {outcome.chosenDriverId ? (
                <> · driver <code className="font-mono">{outcome.chosenDriverId}</code></>
              ) : null}
            </p>
          </div>
        ) : null}
        {outcome?.kind === 'err' ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
            {outcome.message}
          </div>
        ) : null}
      </form>
    </div>
  );
}
